import {
  computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRefs, watch,
} from 'vue';
import { track } from '../tracking/trackingLayer';
import Slider from '../../scripts/lib/slider';
import $ from '../../scripts/lib/jquery';
import { mediaQuery } from '../../scripts/lib/mediaQuery';
import EventBus from '../../scripts/lib/EventBus.js';
import { debounceFn } from '../../scripts/utils.js';
import RecoTracking from '../tracking/recoTracking.js';

const selectorSliderRoot = '.bewerten-slider-outfits';
const selectorSliderNavigation = '.shop-slider__navigation';

const TWO_ITEMS = 2;
const THREE_ITEMS = 3;
const FOUR_ITEMS = 4;
const FIVE_ITEMS = 5;
const responsiveConfig = (hasTabs) => {
  const decIfTabs = (number) => {
    if (hasTabs) {
      return number - 1;
    }
    return number;
  };
  return {
    0: {
      items: TWO_ITEMS,
      slideBy: TWO_ITEMS,
    },
    [mediaQuery.breakpoints.S]: {
      items: decIfTabs(THREE_ITEMS),
      slideBy: decIfTabs(THREE_ITEMS),
    },
    [mediaQuery.breakpoints.M]: {
      items: decIfTabs(THREE_ITEMS),
      slideBy: 1,
    },
    [mediaQuery.breakpoints.XL]: {
      items: THREE_ITEMS,
      slideBy: 1,
    },
    [mediaQuery.breakpoints.XXL]: {
      items: decIfTabs(FOUR_ITEMS),
      slideBy: 1,
    },
    [mediaQuery.breakpoints.XXXL]: {
      items: FOUR_ITEMS,
      slideBy: 1,
    },
    [mediaQuery.breakpoints.fullHD]: {
      items: FIVE_ITEMS,
      slideBy: 1,
    },
  };
};

const cache = {};
const getWithCache = (uri) => {
  if (!cache[uri]) {
    cache[uri] = new Promise((resolve, reject) => {
      fetch(uri)
        .then(res => res.json())
        .then(resolve)
        .catch(reject);
    });
  }
  return cache[uri];
};

const templateElement = document.getElementById('outfits-template');

export default {
  template: templateElement ? templateElement.innerHTML : '',
  props: [
    'outfits', 'trackingStaticData',
  ],
  setup(props) {
    const outfitRef = ref(null);
    const observer = ref(null);
    const resizeObserver = ref(null);
    const resizeObserverTimeout = ref(null);
    const slider = ref(null);

    const state = reactive({
      outfit: undefined,
      initialized: false,
      initDone: false,
      loaded: false,
      visible: false,
      loading: true,
      containerStyles: {},
      tabs: [],
      activeTab: 0,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    watch(() => props, (newProps, oldProps) => {
      const sources = props.outfits;
      const sourcesFiltered = sources.filter(e => !!e);
      if (state.tabs.length === sourcesFiltered.length && state.tabs.every((t, i) => JSON.stringify(t) === JSON.stringify(sourcesFiltered[i]))) {
        return;
      }
      state.activeTab = 0;
      state.tabs = sourcesFiltered;
      if (state.visible.value) {
        if (state.tabs.length > 0) {
          loadOutfitFromUri(state.tabs[0].ansicht);
          initialize();
        }
      } else {
        state.loaded = false;
      }
    }, { deep: true, immediate: true });

    onMounted(() => {
      if (state.tabs.length > 0) {
        initialize();
        window.addEventListener('pageshow', resetTrackingFromBFCache);
      } else {
        state.loading = false;
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', resetTrackingFromBFCache);
      observer.value.disconnect();
      resizeObserver.value.disconnect();
      if (resizeObserverTimeout.value) {
        window.clearTimeout(resizeObserverTimeout);
      }
    });

    function initialize() {
      if (state.initialized) {
        return;
      }

      observer.value = new IntersectionObserver((entries) => {
        state.visible = entries[0].isIntersecting;
        if (state.visible) {
          load();
        }
      }, {
        threshold: 0,
      });
      observer.value.observe(outfitRef.value);

      // temporary resize observer on the outfit container:
      // needed because during initial loading of the outfit component, the container will change height a few times
      // while the images are loaded. this requires a call to 'updateSlider' so that the slider is sized correctly.
      // the listener disconnects 30s after the last observed resize.
      resizeObserver.value = new ResizeObserver(() => {
        updateSlider(true);
        if (resizeObserverTimeout.value) {
          window.clearTimeout(resizeObserverTimeout.value);
        }
        resizeObserverTimeout.value = window.setTimeout(() => {
          resizeObserver.value.disconnect();
        }, 30000);
      });
      resizeObserver.value.observe(outfitRef.value);

      state.initialized = true;
    }

    function load() {
      if (state.loaded.value) {
        return;
      }
      state.loaded = true;
      loadOutfitFromUri(state.tabs[0].ansicht);
    }

    function trackClick() {
      outfitTrack('click');
    }

    function setLoadedAndUpdateSlider(sliderData) {
      state.loading = false;
      state.initDone = true;
      state.outfit = sliderData;
      updateSlider();
    }

    function loadOutfitFromUri(ansicht) {
      if (ansicht.outfitLinkUri === undefined) {
        state.outfit = undefined;
        return;
      }
      if (!state.visible) {
        return;
      }

      state.loading = true;

      getWithCache(ansicht.outfitLinkUri)
        .then((data) => {
          const json = data;
          const sliderData = {
            main: {
              src: json.ansicht.fallbackStandardUrl,
              srcset: `${ansicht.standardUrl} 1x, ${ansicht.standardRetinaUrl} 2x`,
            },
            slides: [],
          };

          if (json.slides.length === 0) {
            EventBus.emit('ents:vue:display-combi-module');
          }

          json.slides.forEach((item) => {
            sliderData.slides.push({
              url: item.url,
              aktiv: item.aktiv || false,
              markenname: item.markenname,
              src: item.ansicht.fallbackStandardUrl,
              srcset: `${item.ansicht.thumbnailUrl} 144w, ${item.ansicht.thumbnailRetinaUrl} 288w, ${item.ansicht.standardUrl} 435w, ${item.ansicht.standardRetinaUrl} 870w`,
              sizes: `(max-width:${mediaQuery.breakpoints.L}px) 50vw, (min-width:${mediaQuery.breakpoints.L + 1}px) 12.5vw)`,
              trackingData: item.trackingData,
            });
          });
          setLoadedAndUpdateSlider(sliderData);
        })
        .catch((e) => {
          console.error('Failed loading outfits: ', e);
          EventBus.emit('ents:vue:display-combi-module');
          const tab = state.tabs[state.activeTab];
          if (tab) {
            const dummySliderData = {
              main: {
                src: tab.ansicht.fallbackStandardUrl,
                srcset: `${tab.ansicht.standardUrl} 1x, ${tab.ansicht.standardRetinaUrl} 2x`,
              },
              // if slides is empty the slider breaks and does not recover
              slides: [{
                url: '#',
                aktiv: true,
                markenname: '',
                src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // transparent
              }],
            };
            setLoadedAndUpdateSlider(dummySliderData);
          }
        });
    }

    function updateSlider(suppressTracking) {
      // set height to prevent layout shifts
      const $outfitContainer = $(outfitRef.value).find('.bewerten-outfit-selector__container');
      if ($outfitContainer.length) {
        state.containerStyles = {
          overflow: 'hidden',
          height: `${$outfitContainer.height()}px`,
        };
      }

      let $navCopy;
      if (slider.value) {
        $navCopy = $(outfitRef.value).find(selectorSliderNavigation).clone();
        slider.value.destroy();
      }

      nextTick(() => {
        if (suppressTracking !== true) {
          outfitTrack('impression');
          initTracking();
        }
        const resetOnImageLoad = ($img) => {
          const resetContainerStyles = () => {
            state.containerStyles = {};
          };
          if ($img.length === 0) {
            resetContainerStyles();
          } else if ($img.get(0).complete) {
            resetContainerStyles();
          } else {
            $img.on('load', resetContainerStyles);
          }
        };

        if (state.outfit && state.outfit.slides && state.outfit.slides.length > 0) {
          // workaround for owl behaviour: it removes its nav on destroy()
          if (!$(outfitRef.value).find(selectorSliderNavigation).length) {
            $(outfitRef.value).find(selectorSliderRoot).append($navCopy);
          }
          const sliderElement = $(outfitRef.value).find(selectorSliderRoot);
          const config = {
            loop: false,
            dots: false,
            responsiveClass: true,
            onInitialized: () => {
              // reset fixed height preventing huge layout shifts
              const $img = sliderElement.find('img').first();
              resetOnImageLoad($img);
            },
            responsive: responsiveConfig(hasTabs.value),
          };
          slider.value = new Slider($(sliderElement), config);
        } else {
          const $img = $(outfitRef.value).find('.bewerten-outfit-selector__bild').first();
          resetOnImageLoad($img);
        }
      });
    }

    function changeTab(index) {
      loadOutfitFromUri(state.tabs[index].ansicht);
      state.activeTab = index;
    }

    const recoTracking = ref(null);

    const initTracking = debounceFn(() => {
      if (recoTracking.value) {
        recoTracking.value.unobserve();
      }
      recoTracking.value = new RecoTracking(
        outfitRef.value.querySelector('.bewerten-outfit-selector__entry--thumbnails'),
        '.shop-slider__slide',
        {
          currency: {
            code: props.trackingStaticData.waehrung,
          },
          promotion_data: {
            type: 'slider',
            name: 'product_styling',
            placement: 'page',
            rule: 'unset',
          },
          event_data: {
            is_interaction_event: false,
          },
        },
      );

      recoTracking.value.observe();
    }, 500);

    function outfitTrack(eventType) {
      track({
        reco_id: 'outfit',
        reco_location: 'pds',
        reco_rule: 'PRODUCT_STYLING_item_page',
        event: `reco.${eventType}`,
      });
    }

    function resetTrackingFromBFCache(e) {
      if (e.persisted && recoTracking.value) {
        initTracking();
      }
    }

    const hasTabs = computed(() => state.tabs.length > 1);

    return {
      hasTabs,
      trackClick,
      changeTab,
      outfitRef,
      ...toRefs(state),
    };
  },
};
